export function preserveCategoryPathSegments(
    categoryPath: string[] | null | undefined,
): string[] {
    if (!categoryPath?.length) {
        return [];
    }

    return categoryPath
        .map((segment) => String(segment).trim())
        .filter(Boolean);
}

export function normalizeCategoryPath(
    categoryPath: string[] | null | undefined,
): string[] {
    if (!categoryPath?.length) {
        return [];
    }

    return categoryPath
        .flatMap((segment) => String(segment).split(/\s+/))
        .map((segment) => segment.trim())
        .filter(Boolean);
}

export function categoryTitleToSlug(title: string) {
    if (!title) {
        return '';
    }

    return title.trim().replace(/\s+/g, '-');
}

export function getCategoryTitle(category: any): string {
    return String(category?.title ?? category?.name ?? '').trim();
}

export type CategoryQueryFormat = 'slash' | 'hyphen';

export function categoryPathToQueryValue(categoryPath: string[]) {
    return categoryPath
        .map((title) => categoryTitleToSlug(title))
        .filter(Boolean)
        .join('/');
}

export function detectCategoryQueryFormat(queryParam: string | null | undefined): CategoryQueryFormat {
    if (!queryParam?.trim()) {
        return 'slash';
    }
    if (queryParam.includes('%') || queryParam.includes('/')) {
        return 'slash';
    }
    return 'hyphen';
}

export function categoryPathToQueryValueWithFormat(categoryPath: string[], format: CategoryQueryFormat = 'slash') {
    if (format === 'hyphen') {
        return categoryPathToHyphenSlug(categoryPath);
    }
    return categoryPathToQueryValue(categoryPath);
}

export function categoryPathMatchesQuerySlug(categoryPath: string[], slug: string) {
    if (!slug) {
        return false;
    }
    return (categoryPathToHyphenSlug(categoryPath) === slug || categoryPathToQueryValue(categoryPath) === slug);
}

export function slugToTitle(slug: string): string {
    return slug.replace(/-/g, ' ').trim();
}

function getCategoryChildren(category: any): any[] {
    return category?.subcategory ?? category?.sub_categories ?? [];
}

export function collectCategoryPathsFromCatalog(
    categories: any[] | null | undefined,
    parentPath: string[] = [],
): string[][] {
    const paths: string[][] = [];
    for (const category of categories ?? []) {
        const title = getCategoryTitle(category);
        if (!title) {
            continue;
        }
        const path = preserveCategoryPathSegments(
            category?.category_path?.length
                ? category.category_path
                : [...parentPath, title],
        );
        if (path.length) {
            paths.push(path);
        }
        paths.push(
            ...collectCategoryPathsFromCatalog(getCategoryChildren(category), path),
        );
    }
    return paths;
}

function greedyParseHyphenJoinedSlug(
    fullSlug: string,
    categories: any[],
): string[] | null {
    const resolved: string[] = [];
    let remaining = fullSlug;
    let level = categories;

    while (remaining) {
        const sorted = [...level].sort(
            (a, b) =>
                categoryTitleToSlug(getCategoryTitle(b)).length -
                categoryTitleToSlug(getCategoryTitle(a)).length,
        );
        let matched = false;

        for (const category of sorted) {
            const title = getCategoryTitle(category);
            const slug = categoryTitleToSlug(title);
            if (!slug) {
                continue;
            }

            if (remaining === slug) {
                resolved.push(title);
                remaining = '';
                matched = true;
                break;
            }

            if (remaining.startsWith(`${slug}-`)) {
                resolved.push(title);
                remaining = remaining.slice(slug.length + 1);
                level = getCategoryChildren(category);
                matched = true;
                break;
            }
        }

        if (!matched) {
            if (resolved.length && remaining) {
                resolved.push(slugToTitle(remaining));
                remaining = '';
                break;
            }

            return null;
        }
    }

    return resolved.length ? resolved : null;
}

export function categoryPathToHyphenSlug(categoryPath: string[]) {
    return categoryPath.map((title) => categoryTitleToSlug(title)).filter(Boolean).join('-');
}

export function parseHyphenJoinedCategorySlug(
    fullSlug: string,
    categories: any[] | null | undefined = [],
): string[] {
    const slug = fullSlug?.trim();
    if (!slug) {
        return [];
    }

    const pool = categories ?? [];
    const knownPaths = collectCategoryPathsFromCatalog(pool);

    for (const path of knownPaths) {
        if (categoryPathToHyphenSlug(path) === slug || categoryPathToQueryValue(path) === slug) {
            return preserveCategoryPathSegments(path);
        }
    }

    const greedy = greedyParseHyphenJoinedSlug(slug, pool);
    if (greedy?.length) {
        return greedy;
    }

    return [slugToTitle(slug)];
}

export function findCategoryByTitle(
    categories: any[] | null | undefined,
    title: string,
): any | null {
    const normalizedTitle = title.trim();
    if (!normalizedTitle) {
        return null;
    }

    for (const category of categories ?? []) {
        if (getCategoryTitle(category) === normalizedTitle) {
            return category;
        }

        const match = findCategoryByTitle(getCategoryChildren(category), normalizedTitle);
        if (match) {
            return match;
        }
    }

    return null;
}

export function reconstructCategoryPathFromSegments(
    segments: string[],
    categories: any[] | null | undefined = [],
): string[] {
    if (!segments.length) {
        return [];
    }

    if (segments.length === 1) {
        return segments;
    }

    const pool = categories ?? [];
    const result: string[] = [];
    let index = 0;
    let level = pool;

    while (index < segments.length) {
        let matchedTitle: string | null = null;
        let nextIndex = index;

        for (let end = segments.length; end > index; end--) {
            const candidate = segments.slice(index, end).join(' ');
            const match =
                level.find((category) => getCategoryTitle(category) === candidate) ??
                findCategoryByTitle(pool, candidate);

            if (match) {
                matchedTitle = candidate;
                nextIndex = end;
                const children = getCategoryChildren(match);
                level = children.length ? children : pool;
                break;
            }
        }

        if (!matchedTitle) {
            result.push(...segments.slice(index));
            break;
        }

        result.push(matchedTitle);
        index = nextIndex;
    }

    return result;
}

function isValidCategoryHierarchy(
    categoryPath: string[],
    categories: any[] | null | undefined,
): boolean {
    let level = categories ?? [];

    for (let index = 0; index < categoryPath.length; index++) {
        const segment = categoryPath[index];
        const match = level.find((category) => getCategoryTitle(category) === segment);
        if (!match) {
            return false;
        }

        if (index === categoryPath.length - 1) {
            return true;
        }

        level = getCategoryChildren(match);
    }

    return false;
}

export function normalizeSingleSegmentPath(
    segment: string,
    categories: any[] | null | undefined,
): string[] {
    const preserved = segment.trim();
    if (!preserved) {
        return [];
    }

    if (!preserved.includes(' ')) {
        return parseHyphenJoinedCategorySlug(preserved, categories);
    }

    const hyphenForm = categoryTitleToSlug(preserved);
    const parsed = parseHyphenJoinedCategorySlug(hyphenForm, categories);
    if (parsed.length > 1) {
        return parsed;
    }

    if (findCategoryByTitle(categories, preserved)) {
        return [preserved];
    }

    return [preserved];
}

export function toApiCategoryPath(
    categoryPath: string[] | null | undefined,
    categories: any[] | null | undefined = [],
): string[] {
    if (!categoryPath?.length) {
        return [];
    }

    let segments = preserveCategoryPathSegments(categoryPath);

    if (segments.length === 1) {
        segments = normalizeSingleSegmentPath(segments[0], categories);
    }

    if (segments.length <= 1) {
        return segments;
    }

    const reconstructed = reconstructCategoryPathFromSegments(segments, categories);
    if (reconstructed.length) {
        return reconstructed;
    }

    const joinedTitle = segments.join(' ');
    if (findCategoryByTitle(categories, joinedTitle)) {
        return [joinedTitle];
    }

    if (isValidCategoryHierarchy(segments, categories)) {
        return segments;
    }

    return segments;
}